// ドックパネルの幅リサイズ。左端のハンドルを掴んで横幅を広げたり縮めたりできる。
//
// パネルは右端に固定されているため、幅 = 画面右端からポインタまでの距離。
// CSS の resize は右下角しか掴めず左端ドラッグには使えないので、自前で実装する。
// 幅はインラインの style.width に書く（未ドラッグ時はクラスの既定幅 25vw のまま）。
import { useCallback, useEffect, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';

/** これ未満はチャットが窮屈になる下限（20rem 相当）。 */
const MIN_WIDTH_PX = 320;
/** 画面の6割まで。本体業務の視界を必ず残す。 */
const MAX_RATIO = 0.6;

export interface UseEdgeResizeResult {
  /** 左端ハンドルの onPointerDown に付けるハンドラ。 */
  onPointerDown: (e: ReactPointerEvent) => void;
}

// 引数は RefObject<HTMLDivElement>（| null なし）。useDrag の ref と型を揃える。
// current は下で null チェックしているので実行時の安全性は変わらない。
export function useEdgeResize(target: RefObject<HTMLDivElement>): UseEdgeResizeResult {
  // ドラッグ中にアンマウントされた場合も window のリスナーを確実に外す
  const detachRef = useRef<() => void>(() => {});
  useEffect(() => () => detachRef.current(), []);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault(); // ドラッグ中のテキスト選択を防ぐ

      const move = (ev: globalThis.PointerEvent) => {
        const el = target.current;
        if (!el) return;
        const max = Math.max(window.innerWidth * MAX_RATIO, MIN_WIDTH_PX);
        const width = Math.min(Math.max(window.innerWidth - ev.clientX, MIN_WIDTH_PX), max);
        el.style.width = `${width}px`;
      };
      const detach = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', detach);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', detach);
      detachRef.current = detach;
    },
    [target]
  );

  return { onPointerDown };
}
